import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBoardStore } from '../../../stores/boardStore';
import { useUserChannelsStore } from '../../../stores/userChannelsStore';
import { boardService } from '../../../lib/api/boards';
import { useEffect, useRef } from 'react';

import { getChartDefinition } from '../components/charts';

export const useBoardFullData = (boardId: string, activePageCharts: any[] = [], activePageType?: string) => {
  const { setBoardData, setCurrencySettings, setDateRange, setIsDataLoading, setFinanceData, setEventsData } = useBoardStore();
  const queryClient = useQueryClient();

  // Calculate required data groups based on charts on the current page
  // We include 'metrics', 'breakdown', 'dailyBreakdown', and 'eventsSummary' in the core groups.
  // This ensures that the first request brings the data needed for ~80% of charts (profit trends, 
  // action markers, etc.) before activePageCharts are even decoded from the response.
  const coreGroups = ['metrics', 'breakdown', 'dailyBreakdown', 'eventsSummary'];
  
  // Custom pages have implicit requirements not captured by chartConfig
  const pageTypeRequirements: Record<string, string[]> = {
    'Performance': ['transactions'],
    'Cash Flow': ['transactions'],
    'Traffic': ['transactions', 'trafficSummary'],
    'Forecast': ['transactions'],
    'Composer': ['transactions', 'trafficSummary', 'detailedBreakdown']
  };

  const requiredGroups = Array.from(new Set([
    ...coreGroups,
    ...(activePageType ? (pageTypeRequirements[activePageType] || []) : []),
    ...(Array.isArray(activePageCharts) ? activePageCharts : []).flatMap(chart => {
      if (!chart) return [];
      const def = getChartDefinition(chart.chartId || chart.type);
      return def?.requiredData || [];
    })
  ]));

  // 1. Unified Backend Payload (Structure + Finance + Events)
  const payloadQuery = useQuery({
    queryKey: ['board-payload', boardId, requiredGroups.sort().join(','), activePageType],
    queryFn: () => boardService.getBoardPayload(boardId, requiredGroups),
    enabled: !!boardId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 30 * 1000,
    // Disable structural sharing so every refetch produces entirely new object references.
    // Many charts use useMemo with deps on nested finance sub-properties (e.g. finance?.dailyBreakdown).
    // With structural sharing, React Query preserves old references for structurally-identical sub-trees,
    // causing those useMemos to skip recomputation even when the data has semantically changed
    // (e.g. a new channel was added but its breakdown entries are identical to the previous fetch).
    structuralSharing: false,
  });

  // Sync Payload to Store
  useEffect(() => {
    if (payloadQuery.data?.success && payloadQuery.data?.data) {
      console.log(`[useBoardFullData] Syncing data to store for board ${boardId}. UpdatedAt: ${payloadQuery.dataUpdatedAt}`);
      const {
        board,
        channels,
        selectedEntitiesForDisplay,
        journals,
        stats,
        specs,
        finance,
        events
      } = payloadQuery.data.data;

      // 1. Update Board Metadata & Structure
      setBoardData({
        board,
        channels,
        journals,
        specs,
        selectedEntitiesForDisplay,
        stats,
        // We will set finance/events separately below to maintain store consistency
      });

      // 2. Sync date range from board
      if (board.startDate && board.endDate) {
        const parseDate = (dateStr: string) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        };
        setDateRange({
          startDate: parseDate(board.startDate),
          endDate: parseDate(board.endDate)
        });
      }

      // 3. Sync currency settings
      const currencySettings = {
        default: board.currency?.default || '',
        conversions: board.currency?.conversions || []
      };
      setCurrencySettings(currencySettings);

      // 4. Update Finance & Events Data
      if (finance) {
        setFinanceData(finance);
        if (finance.errors) {
          useBoardStore.getState().mergeChannelErrors(finance.errors);
        }
      }

      if (events) {
        const eventsData = Array.isArray(events) ? events : (events.events || []);
        const errors = !Array.isArray(events) ? (events.errors || []) : [];
        setEventsData(eventsData);
        if (errors.length > 0) {
          useBoardStore.getState().mergeChannelErrors(errors);
        }
      }
      
      // 5. Trigger derived state update to ensure everything stays in sync after fresh data
      useBoardStore.getState().updateDerivedState();

      // 6. Reset AI verdicts — invalidate page-payload queries so verdicts
      //    refetch with the fresh heuristic based on updated financial data.
      //    Uses invalidateQueries (background refetch) instead of removeQueries
      //    to avoid unnecessary loading skeleton flashes on non-data-changing
      //    refreshes like enrichment (the backend always computes fresh heuristic).
      queryClient.invalidateQueries({ queryKey: ['page-payload', boardId] });
    }
  // Include dataUpdatedAt so the effect fires even when React Query's structural sharing
  // keeps the same data reference (e.g., after cache invalidation returns identical data)
  }, [payloadQuery.data, payloadQuery.dataUpdatedAt, boardId, setBoardData, setCurrencySettings, setDateRange, setFinanceData, setEventsData, queryClient]);

  // Manage loading state:
  // - On initial load (no data yet): set isDataLoading = true
  // - When fetch completes: set isDataLoading = false
  // - During background refetches (enrichment): do NOT override manually-set isDataLoading.
  //   BoardPage sets isDataLoading(true) explicitly for channel additions, entity selections, etc.
  //   We must not override those with false just because React Query has cached data.
  //   Instead, we only clear the flag when fetching finishes.
  useEffect(() => {
    if (!payloadQuery.isFetching && !payloadQuery.isLoading) {
      // Fetch finished — always clear loading state
      setIsDataLoading(false);
    } else if (payloadQuery.isLoading || (payloadQuery.isFetching && !payloadQuery.data)) {
      // Initial load or no cached data — show loader
      setIsDataLoading(true);
    }
    // When isFetching && data exists (background refetch): don't touch isDataLoading.
    // If BoardPage manually set it to true (channel addition), it stays true until fetch completes.
    // If nobody set it (enrichment refetch), it stays false.
  }, [payloadQuery.isFetching, payloadQuery.isLoading, payloadQuery.data, setIsDataLoading]);

  // Clear lingering 'syncing' channel statuses after ANY payload fetch completes.
  // During the populating phase the backend sends channel:sync_status WebSocket
  // events that set userChannelsStore channels to 'syncing'. The backend does NOT
  // send channel:synced for the populating phase — the REST response is the signal.
  // Without this cleanup, the SelectChannelSourceModal would show channels as
  // "fetching data" (grayed out) indefinitely after a date range change.
  const prevFetchingRef = useRef(payloadQuery.isFetching);
  useEffect(() => {
    if (prevFetchingRef.current && !payloadQuery.isFetching) {
      // Clean up userChannelsStore directly — boardStore channels are already
      // set to 'active' by setBoardData (from the REST response) before this
      // effect fires, so checking boardStore would find nothing to clean.
      const userStore = useUserChannelsStore.getState();
      userStore.channels.forEach((ch) => {
        if (ch.status === 'syncing') {
          userStore.updateChannel(String(ch.id), { status: 'active' });
          userStore.setSyncStatusMessage(String(ch.id), '');
        }
      });
    }
    prevFetchingRef.current = payloadQuery.isFetching;
  }, [payloadQuery.isFetching]);

  // Combined return object to mimic original behavior
  const syntheticData = {
    success: payloadQuery.data?.success,
    data: payloadQuery.data?.data || null
  };

  // Only treat as "loading" when there's no data yet (initial load) or when
  // the data is stale and being replaced.  Background refetches triggered by
  // enrichment (channel:enriched) should NOT flash loaders on cards because
  // the board already has valid data — the refetch just adds images/costs.
  const isInitialLoad = payloadQuery.isLoading;
  const isStaleRefetch = payloadQuery.isFetching && !payloadQuery.data;
  const showAsLoading = isInitialLoad || isStaleRefetch;

  return {
    ...payloadQuery,
    data: syntheticData,
    isFinanceLoading: showAsLoading,
    isFinanceError: payloadQuery.isError,
    isEventsLoading: showAsLoading
  };
};
